<template>
  <div>
    <b-spinner
      variant="warning"
      class="m-5"
      style="width: 3rem; height: 3rem"
      v-if="showSpinner"
    ></b-spinner>
    <div v-if="!this.showSpinner" class="addTreatmentPlanRequest">
      <b-row>
        <b-col cols="4">
          <h3 style="float: left">Treatment Plan Request</h3>
        </b-col>
        <b-col cols="4">
          <div class="progressText">
            <b-spinner
              style="width: 1.5rem; height: 1.5rem"
              variant="primary"
              v-if="progressText !== ''"
            ></b-spinner>
            {{ progressText }}
            <b-progress
              v-if="
                progressText === 'Uploading Scan...' ||
                progressText === 'Uploading Treatment Plan...'
              "
              id="idUploadProgress"
              variant="primary"
              :max="uploadMax"
              show-progress
            >
              <b-progress-bar
                :value="uploadProgress"
                :label="`${(uploadProgress / 1000000).toFixed(0)}MB / ${(
                  uploadMax / 1000000
                ).toFixed(0)}MB`"
              ></b-progress-bar>
            </b-progress>
          </div>
          <b-alert
            v-if="status !== 'Cancelled'"
            :show="progressText === ''"
            :variant="unsavedChanges ? 'warning' : 'success'"
          >
            <font-awesome-icon
              :icon="unsavedChanges ? 'exclamation-triangle' : 'check'"
              size="lg"
              :class="{
                'unsaved-changes': unsavedChanges,
                'changes-saved': !unsavedChanges,
              }"
            />
            {{
              unsavedChanges
                ? "You have unsaved changes!"
                : "Everything up-to-date!"
            }}
          </b-alert>
        </b-col>
        <b-col cols="4">
          <div v-if="isInHouseUser" style="float: left">
            <b-button
              @click="onTprAccepted"
              pill
              size="sm"
              id="idAcceptTprBtn"
              variant="primary"
              :disabled="status !== 'Submitted'"
              >Accept</b-button
            >
            <b-button
              v-b-modal="'idRejectTprModal'"
              pill
              size="sm"
              id="idRejectTprBtn"
              variant="primary"
              :disabled="status !== 'Submitted' && status !== 'InDesign'"
              >Reject</b-button
            >
          </div>
          <b-button
            v-if="isFieldUser"
            id="idCloneBtn"
            pill
            size="sm"
            variant="primary"
            :disabled="progressText !== ''"
            @click="cloneClicked"
            >Clone</b-button
          >
          <b-button
            id="idSaveBtn"
            pill
            size="sm"
            variant="primary"
            :disabled="status === 'Cancelled' || progressText !== ''"
            @click="saveClicked"
            >Save</b-button
          >
          <b-button
            v-if="isFieldUser"
            id="idSubmitBtn"
            pill
            size="sm"
            variant="primary"
            :disabled="!tprIsCreatedOrRejected || progressText !== ''"
            @click="submitClicked"
            >Submit</b-button
          >
          <b-button
            v-if="isFieldUser"
            v-b-modal="'idCancelTreatmentPlanRequestdModal'"
            id="idCancelBtn"
            pill
            size="sm"
            variant="primary"
            :disabled="tprIsCancelled || progressText !== ''"
            >Cancel</b-button
          >
        </b-col>
      </b-row>
      <br />
      <!-- Case Details -->
      <b-row>
        <b-col cols="4">
          <b-row>
            <font-awesome-layers
              style="margin-top: -0.5rem; margin-left: -1rem"
              full-width
              class="fa-3x logo"
            >
              <font-awesome-icon icon="square" style="color: #85458a" />
              <font-awesome-icon
                icon="address-card"
                transform="left-1 shrink-7"
                style="color: white"
              />
            </font-awesome-layers>
            <h5 style="float: right">Case Details</h5>
          </b-row>
          <div id="idCaseDetails">
            <b-form-row>
              <label for="idCaseNumber">Case ID</label>
              <div class="w-100"></div>
              <b-link id="idCaseNumber" :href="this.surgicalCaseLink">{{
                this.surgicalCase.metadata.surgicalCaseId
              }}</b-link>
            </b-form-row>
            <b-form-row>
              <label for="idTprID">Treatment Plan Request ID</label>
              <div class="w-100"></div>
              <b-link id="idTprID" href="#">{{ this.descriptor }}</b-link>
            </b-form-row>
            <b-form-row>
              <label for="idBranch">Branch</label>
              <div class="w-100"></div>
              <b-link id="idBranch" href="#">{{ branch }}</b-link>
            </b-form-row>
            <b-form-row>
              <label for="idHospital">Hospital</label>
              <div class="w-100"></div>
              <b-link id="idHospital" href="#">{{ hospitalName }}</b-link>
            </b-form-row>
            <b-form-row>
              <label for="idSurgeon">Surgeon</label>
              <div class="w-100"></div>
              <b-link id="idSurgeon" href="#">{{ surgeonName }}</b-link>
            </b-form-row>
            <b-form-row>
              <b-form-group
                style="margin-top: 1rem; text-align: left"
                label="Covering Reps"
              >
                <b-form-radio-group
                  :disabled="!tprIsCreatedOrRejected || isInHouseUser"
                  style="position: inherit !important"
                  v-model="selectedCoveringRep"
                  :options="coveringReps"
                  name="covering-reps"
                  stacked
                ></b-form-radio-group>
              </b-form-group>
            </b-form-row>
            <b-form-row>
              <b-col cols="10" lg="5">
                <label>Surgery Date</label>
                <b-input-group>
                  <b-form-input
                    id="idSurgeryStartDateInput"
                    v-model="surgeryDateInput"
                    :disabled="tprIsCancelled || isInHouseUser"
                    type="text"
                    size="sm"
                    @blur="onSurgeryDateBlur"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-form-datepicker
                      show-decade-nav
                      id="idSurgeryStartDateBtn"
                      v-model="selectedSurgeryDate"
                      button-only
                      button-variant="outline-dark"
                      right
                      size="sm"
                      aria-controls="idSurgeryStartDateInput"
                      :disabled="tprIsCancelled || isInHouseUser"
                      @context="onSurgeryDateContext"
                    ></b-form-datepicker>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
              <b-col cols="10" lg="5">
                <label>Surgery Time</label>
                <b-input-group>
                  <b-form-input
                    id="idSurgeryStartTimeInput"
                    v-model="surgeryTimeInput"
                    type="text"
                    size="sm"
                    :disabled="tprIsCancelled || isInHouseUser"
                    @blur="onSurgeryTimeBlur"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-form-timepicker
                      id="idSurgeryStartTimeBtn"
                      minutes-step="15"
                      v-model="selectedSurgeryTime"
                      button-only
                      close-button-variant="primary"
                      button-variant="outline-dark"
                      right
                      size="sm"
                      locale="en"
                      aria-controls="idSurgeryStartTimeInput"
                      :disabled="tprIsCancelled || isInHouseUser"
                      @context="onSurgeryTimeContext"
                    ></b-form-timepicker>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
            </b-form-row>
          </div>
        </b-col>
        <!-- TreatmentDetails -->
        <b-col cols="4">
          <b-row>
            <h5 style="float: left">Treatment Details</h5>
          </b-row>
          <b-form-row align-v="end">
            <b-form-group
              style="margin-bottom: 0; margin-top: 1rem; text-align: left"
              :disabled="!tprIsCreatedOrRejected || isInHouseUser"
              label="Operative Side"
            >
              <b-form-checkbox-group
                id="idTreatmentDetailsOperativeSide"
                v-model="selectedOperativeSide"
                required
                @change="onOperativeSideInput"
                :options="operativeSideOptions"
                :disabled="tprIsCancelled || isInHouseUser"
              ></b-form-checkbox-group>
            </b-form-group>
          </b-form-row>
          <b-form-row>
            <label for="idTreatmentDetailsProcedure">Procedure Type</label>
            <b-form-select
              size="sm"
              required
              id="idTreatmentDetailsProcedure"
              v-model="selectedProcedure"
              :options="procedureList"
              :disabled="!tprIsCreatedOrRejected || isInHouseUser"
              @change="onProcedureChanged"
            >
              <template v-slot:first>
                <b-form-select-option :value="null" disabled
                  >-Select-</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-row>
          <b-form-row v-if="this.selectedProcedure">
            <label for="idTreatmentDetailsFirstParam">{{
              this.firstParamLabel
            }}</label>
            <b-form-select
              size="sm"
              required
              id="idTreatmentDetailsFirstParam"
              v-model="selectedFirstParam"
              :options="firstParamList"
              :disabled="!tprIsCreatedOrRejected || isInHouseUser"
            >
              <template v-slot:first>
                <b-form-select-option :value="null" disabled
                  >-Select-</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-row>
          <b-form-row v-if="this.selectedProcedure">
            <label for="idTreatmentDetailsSecondParam">{{
              this.secondParamLabel
            }}</label>
            <b-form-select
              size="sm"
              required
              id="idTreatmentDetailsSecondParam"
              v-model="selectedSecondParam"
              :options="secondParamList"
              :disabled="!tprIsCreatedOrRejected || isInHouseUser"
            >
              <template v-slot:first>
                <b-form-select-option :value="null" disabled
                  >-Select-</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-row>
          <b-form-row v-if="this.selectedProcedure">
            <label for="idTreatmentDetailsThirdParam">{{
              this.thirdParamLabel
            }}</label>
            <b-form-select
              size="sm"
              required
              id="idTreatmentDetailsThirdParam"
              v-model="selectedThirdParam"
              :options="thirdParamList"
              :disabled="!tprIsCreatedOrRejected || isInHouseUser"
            >
              <template v-slot:first>
                <b-form-select-option :value="null" disabled
                  >-Select-</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-row>
          <b-form-row>
            <label for="idNeededBy">Needed By</label>
            <b-input-group>
              <b-form-input
                id="idPatientDetailsNeededByInput"
                v-model="neededByInput"
                autocomplete="off"
                type="text"
                size="sm"
                @blur="onNeededByDateBlur"
                :disabled="!tprIsCreatedOrRejected || isInHouseUser"
              ></b-form-input>
              <b-input-group-append>
                <b-form-datepicker
                  show-decade-nav
                  id="idPatientDetailsNeededBy"
                  v-model="neededBy"
                  button-only
                  button-variant="outline-dark"
                  right
                  size="sm"
                  aria-controls="idPatientDetailsNeededByInput"
                  @context="onNeededByDateContext"
                  :disabled="!tprIsCreatedOrRejected || isInHouseUser"
                ></b-form-datepicker>
              </b-input-group-append>
            </b-input-group>
          </b-form-row>
          <b-form-row>
            <label for="idTreatmentDetailsStatus">Status</label>
            <b-form-input
              id="idTreatmentDetailsStatus"
              size="sm"
              disabled
              v-model="statusDisplay"
            ></b-form-input>
          </b-form-row>
          <b-form-row v-if="this.status === 'Rejected'">
            <label for="idTreatmentDetailsRejectionReason"
              >Rejection Reason</label
            >
            <b-form-textarea
              id="idTreatmentDetailsRejectionReason"
              v-model="rejectionReason"
              disabled
              rows="3"
              max-rows="3"
              size="sm"
            ></b-form-textarea>
          </b-form-row>
          <b-form-row v-if="this.status === 'Cancelled'">
            <label for="idTreatmentDetailsCancelReason">Cancel Reason</label>
            <b-form-textarea
              id="idTreatmentDetailsCancelReason"
              v-model="cancelReason"
              disabled
              rows="3"
              max-rows="3"
              size="sm"
            ></b-form-textarea>
          </b-form-row>
          <b-form-row>
            <label for="idTreatmentDetailsInternalComments"
              >Internal Comments</label
            >
            <b-form-textarea
              id="idTreatmentDetailsInternalComments"
              v-model="internalComments"
              rows="3"
              max-rows="3"
              :disabled="tprIsCancelled"
              size="sm"
            ></b-form-textarea>
          </b-form-row>
          <br />
          <b-form-row v-if="isInHouseUser">
            <b-form-checkbox id="idTprOpened" v-model="tprOpened" />
            <label style="margin-top: 3px">TPR Opened</label>
          </b-form-row>
        </b-col>
        <!-- PatientDetails -->
        <b-col cols="4">
          <b-row>
            <h5 style="float: left">Patient Details</h5>
          </b-row>
          <b-form-row>
            <label for="idPatientDetailsFirstName">First Name</label>
            <b-form-input
              id="idPatientDetailsFirstName"
              size="sm"
              autocomplete="off"
              type="text"
              v-model="firstName"
              :disabled="!tprIsCreatedOrRejected || isInHouseUser"
            ></b-form-input>
          </b-form-row>
          <b-form-row>
            <label for="idPatientDetailsLastName">Last Name</label>
            <b-form-input
              id="idPatientDetailsLastName"
              size="sm"
              autocomplete="off"
              type="text"
              v-model="lastName"
              :disabled="!tprIsCreatedOrRejected || isInHouseUser"
            ></b-form-input>
          </b-form-row>
          <b-form-row>
            <label for="idPatientDetailsDateOfBirth">Date of Birth</label>
            <b-input-group>
              <b-form-input
                id="idPatientDetailsDateOfBirthInput"
                v-model="dateOfBirthInput"
                type="text"
                autocomplete="off"
                size="sm"
                @blur="onDateOfBirthBlur"
                :disabled="!tprIsCreatedOrRejected || isInHouseUser"
              ></b-form-input>
              <b-input-group-append>
                <b-form-datepicker
                  show-decade-nav
                  id="idPatientDetailsDateOfBirth"
                  v-model="dateOfBirth"
                  button-only
                  button-variant="outline-dark"
                  right
                  size="sm"
                  aria-controls="idPatientDetailsDateOfBirthInput"
                  @context="onDateOfBirthContext"
                  :disabled="!tprIsCreatedOrRejected || isInHouseUser"
                ></b-form-datepicker>
              </b-input-group-append>
            </b-input-group>
          </b-form-row>
          <b-form-row>
            <label for="idPatientDetailsGender">Gender</label>
            <b-form-select
              id="idPatientDetailsGender"
              size="sm"
              v-model="gender"
              :options="genderList"
              :disabled="!tprIsCreatedOrRejected || isInHouseUser"
            >
              <template v-slot:first>
                <b-form-select-option :value="null" disabled
                  >-Select-</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-row>
        </b-col>
      </b-row>
      <b-row>
        <!-- offset to stay on right side -->
        <b-col cols="8" offset-md="4">
          <hr />
          <!-- Buttons -->
          <b-row align-h="between" align-v="end">
            <b-col cols="3">
              <b-button
                v-b-modal="'idTreatmentPlanUploadModal'"
                pill
                size="sm"
                id="idUploadTreatmentPlanBtn"
                variant="primary"
                :disabled="
                  status === 'Rejected' ||
                  status === 'Cancelled' ||
                  uploadInProgress
                "
                >Upload Treatment Plan</b-button
              >
            </b-col>
            <b-col cols="4">
              <h5>Attach Patient Scan</h5>
              <b-button
                v-b-modal="'idUploadScanModal'"
                pill
                size="sm"
                id="idFromComputerBtn"
                variant="primary"
                :disabled="
                  tprIsCancelled ||
                  isInHouseUser ||
                  tprWasSubmitted ||
                  uploadInProgress
                "
                >From Computer</b-button
              >
              <b-button
                v-b-modal="'idFromCloudModal'"
                pill
                size="sm"
                id="idFromCloudBtn"
                variant="primary"
                :disabled="
                  tprIsCancelled ||
                  isInHouseUser ||
                  tprWasSubmitted ||
                  uploadInProgress
                "
                >From Cloud</b-button
              >
              <UploadScanModal @okClicked="onUploadPatientScan" />
              <TreatmentPlanRequestAttachScanModal
                @added="onAttachPatientScan"
              />
              <TreatmentPlanRequestUploadPlanModal
                :implantData="applicationImplantData"
                :patientDetailsFilled="patientDetailsFilled"
                @okClicked="onUploadTreatmentPlan"
              />
              <TreatmentPlanRequestCancelModal
                :tprId="this.descriptor"
                :applicationType="this.selectedProcedure"
                @tprCancelled="onTprCancelled"
              />
              <TreatmentPlanRequestRejectModal
                :tprId="this.descriptor"
                :applicationType="this.selectedProcedure"
                @tprRejected="onTprRejected"
                @tprAccepted="onTprAccepted"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-card style="width: 100%">
              <b-card-title id="idAddedPatientScansCard" title-tag="h5">
                Added Patient Scans
                <b-button
                  variant="primary"
                  id="idDeletePatientScansBtn"
                  pill
                  size="sm"
                  :disabled="
                    (status !== 'Rejected' && status !== 'Created') ||
                    isInHouseUser ||
                    selectedPatientScans.length === 0
                  "
                  @click="deleteSelectedPatientScans"
                  >Delete</b-button
                >
              </b-card-title>
              <b-table
                head-variant="light"
                id="idAddedPatientScansTable"
                show-empty
                small
                responsive
                outlined
                :selectable="!tprWasSubmitted && !tprIsCancelled"
                select-mode="multi"
                :items="addedPatientScans"
                :fields="addedpatientScansFields"
                @row-selected="onPatientScanSelected"
              >
                <template v-slot:cell(active)="row">
                  <b-form-checkbox
                    :disabled="
                      (status !== 'Created' && status !== 'Rejected') ||
                      isInHouseUser
                    "
                    size="lg"
                    v-model="row.item.active"
                    @change="onPatientScanActivated(row.item)"
                  ></b-form-checkbox>
                </template>
                <template v-slot:cell(actions)="row">
                  <!-- Dicom Viewer Button - all buttons with all styles initialized, but only one visible at a time -->
                  <b-button
                    class="viewer-btn"
                    v-if="row.item.provisioningStatus === 'Unprovisioned'"
                    size="sm"
                    @click="handleDicomButtonClick(row.item)"
                    variant="outline-primary"
                  >
                    <b-icon scale=".9" icon="eye"></b-icon>
                    Click to Load
                  </b-button>

                  <!-- Completed Button -->
                  <b-button
                    class="viewer-btn"
                    v-else-if="row.item.provisioningStatus === 'Completed'"
                    size="sm"
                    @click="handleDicomButtonClick(row.item)"
                    variant="outline-success"
                  >
                    <b-icon scale=".9" icon="check-circle-fill"></b-icon>
                    Click to View
                  </b-button>

                  <!-- Failed Button -->
                  <b-button
                    class="viewer-btn"
                    v-else-if="row.item.provisioningStatus === 'Failed'"
                    size="sm"
                    @click="handleDicomButtonClick(row.item)"
                    variant="outline-danger"
                  >
                    <b-icon scale=".9" icon="x-circle-fill"></b-icon>
                    Click to Retry
                  </b-button>

                  <!-- Purging Button -->
                  <b-button
                    class="viewer-btn"
                    v-else-if="row.item.provisioningStatus === 'Purging'"
                    disabled
                    size="sm"
                    @click="handleDicomButtonClick(row.item)"
                    variant="outline-primary"
                  >
                    <b-icon
                      scale=".9"
                      icon="trash-fill"
                      animation="throb"
                    ></b-icon>
                    Purging...</b-button
                  >
                  <!-- For provisioning statuses InProgress or status not set yet, show the loading button -->
                  <b-button
                    class="viewer-btn"
                    v-else
                    size="sm"
                    disabled
                    variant="outline-primary"
                  >
                    <b-icon
                      scale=".9"
                      icon="arrow-clockwise"
                      animation="spin"
                    ></b-icon>
                    Loading...
                  </b-button>

                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="downloadPatientScanClicked(row.item)"
                    ><b-icon scale=".9" icon="download"></b-icon>
                    Download</b-button
                  >
                </template>
              </b-table>
            </b-card>
          </b-row>
          <b-row>
            <b-card
              id="idAddedTreatmentPlanCard"
              title-tag="h5"
              style="width: 100%"
              title="Added Treatment Plan"
            >
              <b-table
                head-variant="light"
                id="idAddedTreatmentPlanTable"
                show-empty
                small
                responsive
                outlined
                :items="addedTreatmentPlanComputed"
                :fields="addedTreatmentPlanFields"
              >
                <template v-slot:cell(action)="row">
                  <b-button
                    :disabled="
                      status !== 'TreatmentPlanAvailable' &&
                      status !== 'TreatmentPlanDelivered'
                    "
                    size="sm"
                    variant="outline-primary"
                    @click="downloadTreatmentPlanClicked(row.item)"
                    >Download</b-button
                  >
                </template>
              </b-table>
            </b-card>
          </b-row>
          <b-row>
            <b-card
              id="idTreatmentPlanRequestHistoryCard"
              title-tag="h5"
              title="Treatment Plan Request History"
              style="width: 100%"
            >
              <b-table
                head-variant="light"
                id="idTreatmentPlanRequestHistoryTable"
                show-empty
                small
                responsive
                outlined
                :items="tprHistory"
                :fields="tprHistoryFields"
              ></b-table>
            </b-card>
          </b-row>
          <b-row>
            <b-card id="idEmailsCard" title-tag="h5" title="Emails">
              <b-table
                head-variant="light"
                id="idEmailsTable"
                show-empty
                small
                fixed
                outlined
                :items="emails"
              ></b-table>
            </b-card>
          </b-row>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import TreatmentPlanRequestCancelModal from "@/components/TreatmentPlanRequestCancelModal";
import UploadScanModal from "@/components/UploadScanModal";
import TreatmentPlanRequestUploadPlanModal from "@/components/TreatmentPlanRequestUploadPlanModal";
import TreatmentPlanRequestAttachScanModal from "@/components/TreatmentPlanRequestAttachScanModal";
import TreatmentPlanRequestRejectModal from "@/components/TreatmentPlanRequestRejectModal.vue";

import { download } from "../mixins/download";
import { zip } from "../mixins/zip";

import moment from "moment";
import _ from "lodash";
import axios from "axios";

import * as rax from "retry-axios";

export default {
  mixins: [download, zip],
  data() {
    return {
      totalTimeVisible: 1,
      tabFocus: true,
      unsavedTreatmentPlan: false,
      rejectionReason: "",
      cancelReason: "",
      changeString: "",
      internalComments: "",
      isFieldUser: false,
      unsavedChanges: false,
      surgeryDateInput: null,
      surgeryTimeInput: null,
      dateOfBirthInput: null,
      neededByInput: null,
      isInHouseUser: false,
      showSpinner: true,
      addedTreatmentPlan: null,
      tpr: null,
      uploadProgress: null,
      uploadMax: null,
      firstName: "",
      lastName: "",
      gender: "",
      progressText: "",
      selectedCoveringRep: null,
      selectedOperativeSide: [],
      operativeSideOptions: [
        { text: "Left", value: "left" },
        { text: "Right", value: "right" },
      ],
      procedureList: [""],
      selectedProcedure: this.tpr?.treatment?.application?.applicationType
        ? this.tpr.treatment.application.applicationType
        : null,
      selectedFirstParam: null,
      selectedSecondParam: null,
      selectedThirdParam: null,
      genderList: [
        { text: "Male", value: "male" },
        { text: "Female", value: "female" },
      ],
      status: "",
      statusDisplay: "",
      tprOpened: null,
      addedPatientScans: [],
      addedpatientScansFields: [
        { key: "patientScan" },
        { key: "assignedBy" },
        { key: "uploadedDate" },
        { key: "active" },
        { key: "actions" },
      ],
      addedTreatmentPlanFields: [
        { key: "fileName" },
        { key: "assignedBy", formatter: this.formatGuidsToStryker },
        { key: "implantSizes" },
        { key: "action", label: "" },
      ],
      selectedPatientScans: [],
      tprHistory: [],
      tprHistoryFields: [
        { key: "Date" },
        { key: "Status" },
        { key: "User", formatter: this.formatGuidsToStryker },
      ],
      emails: [],
      dateOfBirth: null,
      neededBy: null,
      selectedSurgeryDate: null,
      selectedSurgeryTime: null,
      requestId: null,
      newTpr: null,
      applicationParameterSpec: null,
      statusChecksTPRTimer: null,
    };
  },
  props: ["surgicalCase", "selectedTpr"],
  components: {
    TreatmentPlanRequestCancelModal,
    TreatmentPlanRequestRejectModal,
    UploadScanModal,
    TreatmentPlanRequestUploadPlanModal,
    TreatmentPlanRequestAttachScanModal,
  },
  mounted() {
    this.unsavedChanges = false;
  },
  beforeDestroy() {
    // Stop the update timer before we leave the page
    clearInterval(this.statusChecksTPRTimer);
  },
  async created() {
    this.applicationParameterSpec =
      this.$store.state.appParameterSpec.Applications;
    this.isFieldUser = this.$store.state.roles?.includes("RH.MS.FieldUser");
    this.isInHouseUser =
      this.$store.state.roles?.includes("RH.MS.InhouseUser") &&
      !this.$store.state.roles?.includes("RH.MS.FieldUser");

    this.showSpinner = true;
    // Error if user tries to access TPR which doesn't exist or they don't have access to
    if (!this.selectedTpr) {
      this.$root.$emit("showAlert", {
        type: "error",
        msg: `Fatal error in retrieving Treatment Plan Request. Please check that the link is correct and that you have the correct access rights.`,
      });
    }
    //TODO: Use promise array for parallel execution of API calls
    this.tpr = this.selectedTpr;
    // Check if request is in cancelled state
    this.requestId = this.tpr.metadata.requestId;
    // Get Descriptor
    this.descriptor = this.tpr.externalReference;
    // Set Surgical Case Link based on TPR ID
    this.surgicalCaseLink =
      this.$redirectUri +
      "/surgicalCase?id=" +
      this.surgicalCase.metadata.surgicalCaseId;

    // Get Surgery Date and Time
    this.selectedSurgeryDate = moment(
      this.surgicalCase.surgeryStartDateTime
    ).format("YYYY-MM-DD");
    this.selectedSurgeryTime = moment(
      this.surgicalCase.surgeryStartDateTime
    ).format("HH:mm:ss");

    // Handle TPR Status
    this.status = this.tpr.metadata.commentedRequestState.requestState;
    this.statusDisplay = this.adjustStatusDisplay(this.status);
    if (this.tpr.metadata.commentedRequestState.requestState === "Rejected")
      this.rejectionReason = this.tpr.metadata.commentedRequestState.comment;

    if (this.tpr.metadata.commentedRequestState.requestState === "Cancelled")
      this.cancelReason = this.tpr.metadata.commentedRequestState.comment;

    // Get Operative Side: Use from existing TPR or from Surgical Case
    if (this.tpr?.treatment?.operativeSide) {
      this.selectedOperativeSide = [this.tpr.treatment.operativeSide];
    } else if (this.surgicalCase.operativeSide)
      this.selectedOperativeSide = [this.surgicalCase.operativeSide];

    // Check for existing Patient Information
    if (this.tpr?.patientInfo?.id) {
      const patientInfoResponse = await this.$patientInfoApi.patientinfoIdGet(
        this.tpr.patientInfo.id
      );

      this.gender = patientInfoResponse.sex || null;
      this.firstName = patientInfoResponse.firstName || null;
      this.lastName = patientInfoResponse.lastName || null;

      this.dateOfBirth = patientInfoResponse.dateOfBirth
        ? moment(
            new Date(
              patientInfoResponse.dateOfBirth.getUTCFullYear(),
              patientInfoResponse.dateOfBirth.getUTCMonth(),
              patientInfoResponse.dateOfBirth.getUTCDate()
            )
          ).format("YYYY-MM-DD")
        : null;
    }
    // Check for needBy Date in TPR
    this.neededBy = this.tpr?.needByDate
      ? moment(this.tpr.needByDate).utc().format("YYYY-MM-DD")
      : null;

    // Handle Additional Data
    this.tprOpened = this.tpr?.additionalData?.tprOpened;
    this.internalComments = this.tpr?.additionalData?.internalComments;

    // Check for primary contact (Selected Covering Rep)
    this.selectedCoveringRep = this.tpr.primaryContact
      ? this.tpr.primaryContact.email
      : null;

    // Autoselect MPS if only one available
    if (
      !this.selectedCoveringRep &&
      this.surgicalCase.coveringReps.length === 1
    ) {
      this.selectedCoveringRep = this.surgicalCase.coveringReps[0].email;
    }

    // Get all Available Procedures
    this.procedureList = this.$store.getters.procedureList;
    // Get Application Settings if exist
    if (this.tpr?.treatment?.application?.applicationType) {
      this.selectedProcedure = this.tpr.treatment.application.applicationType;
      this.selectedFirstParam =
        this.tpr.treatment.application.applicationParameters[0].value;
      this.selectedSecondParam =
        this.tpr.treatment.application.applicationParameters[1].value;
      this.selectedThirdParam =
        this.tpr.treatment.application.applicationParameters[2].value;
    }

    // Added Patient Scans
    try {
      if (this.tpr?.patientScan?.id) {
        const psResponse =
          await this.$patientScansMetadataApi.patientscansIdMetadataGet(
            this.tpr.patientScan.id
          );
        let uploadedBy = "";

        // For automatic uploads get Scan Facility name
        if (!psResponse.metadata.isManualUpload) {
          try {
            const facility = await this.$buhApi.branchesScanfacilitiesIdGet(
              psResponse.metadata.uploadedByScanFacility
            );
            uploadedBy = facility.facilityName;
          } catch (e) {
            uploadedBy = "Stryker";
          }
        } else {
          uploadedBy = psResponse.metadata.uploadedByUser;
        }

        this.addedPatientScans.push({
          id: psResponse.metadata.patientScanId,
          patientScan: psResponse.metadata.patientScanDescriptor,
          assignedBy: uploadedBy,
          uploadedDate: moment(psResponse.metadata.dateTimeUploaded)
            .utc()
            .format("l"),
          active: true,
          provisioningStatus: "InProgress",
        });
      }
      if (
        this.tpr?.otherPatientScans &&
        this.tpr?.otherPatientScans.length > 0
      ) {
        for (const scan of this.tpr.otherPatientScans) {
          const psResponse =
            await this.$patientScansMetadataApi.patientscansIdMetadataGet(
              scan.id
            );
          let uploadedBy = "";
          // For automatic uploads get Scan Facility name
          if (!psResponse.metadata.isManualUpload) {
            try {
              const facility = await this.$buhApi.branchesScanfacilitiesIdGet(
                psResponse.metadata.uploadedByScanFacility
              );
              uploadedBy = facility.facilityName;
            } catch (e) {
              uploadedBy = "Stryker";
            }
          } else {
            uploadedBy = psResponse.metadata.uploadedByUser;
          }
          this.addedPatientScans.push({
            id: psResponse.metadata.patientScanId,
            patientScan: psResponse.metadata.patientScanDescriptor,
            assignedBy: uploadedBy,
            uploadedDate: moment(psResponse.metadata.dateTimeUploaded)
              .utc()
              .format("l"),
            active: false,
            provisioningStatus: "InProgress",
          });
        }
      }
    } catch (e) {
      this.$root.$emit("showAlert", {
        type: "error",
        msg: "Error " + e.status + " while retrieving Patient Scan Information",
      });
      this.progressText = "";
      // eslint-disable-next-line
      console.log(e);
    }

    // Added Treatment Plan
    try {
      const addedTreatmentPlans =
        await this.$treatmentPlanMetadataApi.treatmentplansMetadataGet({
          associatedTreatmentPlanRequestId: this.tpr.metadata.requestId,
          latestUploadedOnly: true,
        });
      this.addedTreatmentPlan = addedTreatmentPlans[0];
    } catch (e) {
      this.$root.$emit("showAlert", {
        type: "error",
        msg: "Error " + e.status + " while retrieving Added Treatment Plan",
      });
      this.progressText = "";
      // eslint-disable-next-line
      console.log(e);
    }

    // TPR History
    try {
      this.updateTprHistory(this.tpr.metadata.requestId);
    } catch (e) {
      this.$root.$emit("showAlert", {
        type: "error",
        msg:
          "Error " +
          e.status +
          " while retrieving Treatment Plan Request History",
      });
      this.progressText = "";
      // eslint-disable-next-line
      console.log(e);
    }
    window.addEventListener(
      "beforeunload",
      (e) => {
        if (
          this.progressText === "Uploading Scan..." ||
          this.progressText === "Uploading Treatment Plan..."
        ) {
          e.preventDefault();
          e.returnValue = "";
        }
      },
      false
    );
    // Done loading all elements, show data
    this.showSpinner = false;
    this.unsavedChanges = false;

    // Check the provisioning statuses after done loading so we don't block it
    await this.getStatusesForScans(true);

    // After done getting all the scans, start the interval to check the statuses periodically
    clearInterval(this.statusChecksTPRTimer);
    this.startBackendStatusChecks();
  },
  methods: {
    formatGuidsToStryker(value) {
      if (
        value &&
        value.match(
          "^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$"
        )
      ) {
        return "Stryker";
      } else if (value && value === "Treatment Plan Upload Crawler") {
        return "Stryker";
      } else {
        return value;
      }
    },
    async onTprRejected(reason) {
      this.progressText = "Rejecting Request...";
      try {
        const additionalDataResponse =
          await this.$additionalDataApi.treatmentplanrequestsIdAdditionalDataPatch(
            this.tpr.metadata.requestId,
            this.tpr.metadata.dateTimeLastModified,
            { tprOpened: false }
          );
        this.tprOpened = false;
        this.requestId = additionalDataResponse.requestId;
        const stateResponse =
          await this.$treatmentPlanRequestsStateApi.treatmentplanrequestsIdStatePut(
            this.requestId,
            "Rejected",
            { requestStateComment: reason }
          );
        this.tpr.metadata = stateResponse;
        this.status = stateResponse.commentedRequestState.requestState;
        this.statusDisplay = this.adjustStatusDisplay(this.status);
        this.rejectionReason = stateResponse.commentedRequestState.comment;
        await this.updateTprHistory(this.requestId);
      } catch (error) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${error.status} while rejecting the Request`,
        });
        this.progressText = "";
        // eslint-disable-next-line
        console.log(error);
        return;
      }
      this.progressText = "";
      this.$root.$emit("showAlert", {
        type: "info",
        msg: `Request rejected`,
      });
    },
    async onTprAccepted() {
      this.progressText = "Accepting Request...";
      try {
        const stateResponse =
          await this.$treatmentPlanRequestsStateApi.treatmentplanrequestsIdStatePut(
            this.requestId,
            "InDesign"
          );
        this.status = stateResponse.commentedRequestState.requestState;
        this.statusDisplay = this.adjustStatusDisplay(this.status);
        this.tpr.metadata = stateResponse;
        await this.updateTprHistory(this.requestId);
      } catch (error) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${error.status} while accepting the Request`,
        });
        this.progressText = "";
        // eslint-disable-next-line
        console.log(error);
        return;
      }

      this.progressText = "";
      this.$root.$emit("showAlert", {
        type: "info",
        msg: `Request accepted`,
      });
    },
    async onPatientScanActivated(item) {
      this.progressText = "Updating Patient Information...";
      this.unsavedChanges = true;
      this.firstName = "";
      this.lastName = "";
      this.gender = null;
      this.dateOfBirth = null;
      this.dateOfBirthInput = null;

      this.addedPatientScans.map((scan) => {
        if (scan.id !== item.id) scan.active = false;
      });
      try {
        const scanResponse =
          await this.$patientScansMetadataApi.patientscansIdMetadataGet(
            item.id
          );
        if (scanResponse.patient.patientName) {
          var namesCount = scanResponse.patient.patientName.match(/\^/g);
          if (!namesCount) {
            // Only last name
            this.lastName = scanResponse.patient.patientName;
          } else {
            // char "^" found, so we need to split
            var [lastName, firstName] =
              scanResponse.patient.patientName.split(/\^(.+)/);
            this.firstName = firstName;
            this.lastName = lastName;
          }
        }
        if (scanResponse.patient.patientSex) {
          if (scanResponse.patient.patientSex === "F") this.gender = "female";
          else if (scanResponse.patient.patientSex === "M")
            this.gender = "male";
          else this.gender = null;
        }
        if (scanResponse.patient.patientDateOfBirth) {
          this.dateOfBirth = moment(
            new Date(
              scanResponse.patient.patientDateOfBirth.getUTCFullYear(),
              scanResponse.patient.patientDateOfBirth.getUTCMonth(),
              scanResponse.patient.patientDateOfBirth.getUTCDate()
            )
          ).format("YYYY-M-D");
        }
      } catch (error) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${error.status} while updating Patient Information`,
        });
        this.progressText = "";
        // eslint-disable-next-line
        console.log(error);
        return;
      }
      this.progressText = "";
    },
    async deleteSelectedPatientScans() {
      // Get Submit confirmation
      const confirmMsg =
        "Are you sure you want to delete selected patient scans?";
      const deleteConfirm = await this.$bvModal.msgBoxConfirm(confirmMsg, {
        title: "",
        size: "md",
        buttonSize: "md",
        okVariant: "primary",
        okTitle: "Accept",
        cancelVariant: "outline-primary",
        cancelTitle: "Cancel",
        footerClass: "p-2",
        hideHeaderClose: false,
        centered: true,
      });
      if (!deleteConfirm) return;

      // Start deleting (detach)
      this.addedPatientScans = this.addedPatientScans.filter(
        (scan) => !this.selectedPatientScans.includes(scan)
      );
    },
    onPatientScanSelected(items) {
      this.selectedPatientScans = items;
    },
    onOperativeSideInput(input) {
      if (input.length > 1) this.selectedOperativeSide = [input[1]];
    },
    onSurgeryDateBlur() {
      if (
        this.surgeryDateInput &&
        this.surgeryDateInput.match(/[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2,4}/g)
      ) {
        this.selectedSurgeryDate = moment(this.surgeryDateInput).format(
          "YYYY-M-D"
        );
      }
    },
    onSurgeryDateContext(ctx) {
      if (ctx.selectedDate) {
        this.surgeryDateInput = moment(ctx.selectedDate).format("M/D/YYYY");
      }
    },
    onSurgeryTimeBlur() {
      if (
        this.surgeryTimeInput &&
        this.surgeryTimeInput.match(/[0-9]{1,2}:[0-9]{1,2}/g)
      ) {
        this.surgeryTimeInput = moment(this.surgeryTimeInput, "LT").format(
          "LT"
        );
        this.selectedSurgeryTime = moment(this.surgeryTimeInput, "LT").format(
          "HH:mm:ss"
        );
      }
    },
    onSurgeryTimeContext(ctx) {
      if (ctx.hours !== null && ctx.minutes !== null) {
        this.surgeryTimeInput = ctx.formatted;
      }
    },
    onDateOfBirthBlur() {
      if (
        this.dateOfBirthInput &&
        this.dateOfBirthInput.match(/[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2,4}/g)
      ) {
        this.dateOfBirth = moment(this.dateOfBirthInput).format("YYYY-M-D");
      }
    },
    onDateOfBirthContext(ctx) {
      if (ctx.selectedDate) {
        this.dateOfBirthInput = moment(ctx.selectedDate).format("M/D/YYYY");
      }
    },
    onNeededByDateBlur() {
      if (
        this.neededByInput &&
        this.neededByInput.match(/[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2,4}/g)
      ) {
        this.neededBy = moment(this.neededByInput).format("YYYY-M-D");
      }
    },
    onNeededByDateContext(ctx) {
      if (ctx.selectedDate) {
        this.neededByInput = moment(ctx.selectedDate).format("M/D/YYYY");
      }
    },
    onProcedureChanged() {
      this.selectedFirstParam = null;
      this.selectedSecondParam = null;
      this.selectedThirdParam = null;
    },
    onAttachPatientScan(scan) {
      if (this.addedPatientScans.filter((el) => el.id === scan.id).length > 0) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: "Scan already attached",
        });
      } else {
        this.addedPatientScans.unshift({
          id: scan.id,
          patientScan: scan.fileName,
          assignedBy: scan.Uploader,
          uploadedDate: scan.ModificationDate,
          active: true,
          provisioningStatus: "InProgress",
        });
        this.onPatientScanActivated(
          this.addedPatientScans.filter((el) => el.id === scan.id)[0]
        );
      }
    },
    async updateTprHistory(requestId) {
      this.$treatmentPlanRequestsStateApi
        .treatmentplanrequestsIdStateHistoryGet(requestId)
        .then(
          (response) =>
            (this.tprHistory = response.map((state) => {
              // Manually put spaces in and change the wording of the TPR status
              // for displaying in the table
              var tprStatus = state.commentedRequestState.requestState;
              tprStatus = this.adjustStatusDisplay(tprStatus);
              return {
                Date: moment(state.stateChangeTimestamp).format(
                  "M/D/YYYY, h:mm A"
                ),
                Status: tprStatus,
                User: state.stateChangedBy,
              };
            }))
        );
    },
    async downloadPatientScanClicked(rowItem) {
      this.progressText = "Preparing Download...";
      try {
        await this.downloadPatientScan(rowItem.id, rowItem.patientScan);
        if (this.isInHouseUser & this.tprWasSubmitted) {
          const additionalDataResponse =
            await this.$additionalDataApi.treatmentplanrequestsIdAdditionalDataPatch(
              this.tpr.metadata.requestId,
              this.tpr.metadata.dateTimeLastModified,
              { tprOpened: true }
            );
          this.tprOpened = true;
          this.requestId = additionalDataResponse.requestId;
          this.tpr.metadata = additionalDataResponse;
        }
      } catch (error) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: "Error while downloading Patient Scan",
        });
        this.progressText = "";
        // eslint-disable-next-line
        console.log(error);
      }
      this.progressText = "";
    },
    async downloadTreatmentPlanClicked(rowItem) {
      this.progressText = "Preparing Download...";
      try {
        await this.downloadTreatmentPlan(
          rowItem.treatmentPlanId,
          rowItem.fileName
        );
        const stateResponse =
          await this.$treatmentPlanRequestsStateApi.treatmentplanrequestsIdStatePut(
            this.requestId,
            "TreatmentPlanDelivered"
          );
        this.tpr.metadata = stateResponse;
        this.status = stateResponse.commentedRequestState.requestState;
        this.statusDisplay = this.adjustStatusDisplay(this.status);
        this.updateTprHistory(this.requestId);
      } catch (error) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: "Error while downloading Treatment Plan",
        });
        this.progressText = "";
        // eslint-disable-next-line
        console.log(error);
      }
      this.progressText = "";
    },
    async createTprDto() {
      var tprDto = {};
      tprDto.treatment = {};
      tprDto.treatment.application = {};
      tprDto.otherPatientScans = [];
      var patientInfoResponse = null;
      // 1. Handle Patient Information
      if (this.tprWasSubmitted) {
        // Do not update Patient info, no change allowed
        tprDto.patientInfo = {};
        // And update TPR with patient info
        tprDto.patientInfo.id = this.tpr.patientInfo.id;
        tprDto.patientInfo.uri = "http://" + this.tpr.patientInfo.id;
      } else {
        if (
          this.firstName ||
          this.lastName ||
          this.gender ||
          this.dateOfBirth
        ) {
          if (
            this.firstName &&
            this.lastName &&
            this.gender &&
            this.dateOfBirth
          ) {
            var patientInfoDto = {};

            // 1.1 copy all patient info
            patientInfoDto.firstName = this.firstName;
            patientInfoDto.lastName = this.lastName;
            patientInfoDto.sex = this.gender;
            patientInfoDto.dateOfBirth = moment(this.dateOfBirth).format(
              "YYYY-MM-DD"
            );
            // 1.2 create new patient information
            try {
              patientInfoResponse = await this.$patientInfoApi.patientinfoPost(
                patientInfoDto
              );
            } catch (e) {
              this.$root.$emit("showAlert", {
                type: "error",
                msg:
                  "Error " + e.status + " while creating patient information.",
              });
              throw e;
            }
          } else {
            this.$root.$emit("showAlert", {
              type: "error",
              msg: "Please fill out or select the following required fields: Patient Details",
            });
            throw new Error("Missing Patient Information");
          }
          if (patientInfoResponse) {
            this.tpr.patientInfo = {};
            this.tpr.patientInfo.id = patientInfoResponse.patientInfoId;
            this.tpr.patientInfo.uri =
              "http://" + patientInfoResponse.patientInfoId;
            tprDto.patientInfo = {};
            // And update TPR with patient info
            tprDto.patientInfo.id = patientInfoResponse.patientInfoId;
            tprDto.patientInfo.uri =
              "http://" + patientInfoResponse.patientInfoId;
          }
        }
      }
      // 2. Handle TPR

      // 2.1 Transfer Surgical Case Information to TPR
      tprDto.externalReference = this.descriptor;

      tprDto.region = this.surgicalCase.branchName;
      tprDto.additionalData = {
        internalComments: this.internalComments,
        tprOpened: this.tprOpened,
      };
      tprDto.treatment.hospitalName = this.surgicalCase.hospitalName;
      tprDto.treatment.hospitalReference = this.surgicalCase.hospitalNumber;
      tprDto.treatment.surgeonName = this.surgicalCase.surgeon;
      tprDto.treatment.operativeSide =
        this.selectedOperativeSide.length > 0
          ? this.selectedOperativeSide[0]
          : null;
      tprDto.treatment.surgeryDate = this.selectedSurgeryDate;

      // 2.2 Primary Contact and other Contacts
      if (this.selectedCoveringRep) {
        const selectedRepName = this.coveringReps.filter((rep) => {
          if (rep.value === this.selectedCoveringRep) {
            return rep;
          }
        })[0];
        var [firstName, lastName] = selectedRepName.text.split(" ");
        tprDto.primaryContact = {
          firstName: firstName,
          lastName: lastName,
          email: this.selectedCoveringRep,
        };
      }

      tprDto.otherContacts = [];
      for (const rep of this.coveringReps) {
        if (rep.value !== this.selectedCoveringRep) {
          const [firstName, lastName] = rep.text.split(" ");
          tprDto.otherContacts.push({
            firstName: firstName,
            lastName: lastName,
            email: rep.value,
          });
        }
      }
      // 2.3 neededByDate needs to be copied if set
      if (this.neededBy)
        tprDto.needByDate = moment(this.neededBy).format("YYYY-MM-DD");

      // 2.4 Add Scans (including Other Scans)
      this.addedPatientScans.forEach((scan) => {
        if (scan.active) {
          tprDto.patientScan = { id: scan.id };
        } else {
          tprDto.otherPatientScans.push({ id: scan.id });
        }
      });

      // 2.5 Create Application Data
      if (
        this.selectedProcedure ||
        this.selectedFirstParam ||
        this.selectedSecondParam ||
        this.selectedThirdParam
      ) {
        if (
          this.selectedProcedure &&
          this.selectedFirstParam &&
          this.selectedSecondParam &&
          this.selectedThirdParam
        ) {
          tprDto.treatment.application.applicationType = this.selectedProcedure;
          tprDto.treatment.application.applicationParameters = [];
          tprDto.treatment.application.applicationParameters.push({
            value: this.selectedFirstParam,
            key: this.selectedFirstParam.substr(
              0,
              this.selectedFirstParam.lastIndexOf(".")
            ),
          });
          tprDto.treatment.application.applicationParameters.push({
            value: this.selectedSecondParam,
            key: this.selectedSecondParam.substr(
              0,
              this.selectedSecondParam.lastIndexOf(".")
            ),
          });
          tprDto.treatment.application.applicationParameters.push({
            value: this.selectedThirdParam,
            key: this.selectedThirdParam.substr(
              0,
              this.selectedThirdParam.lastIndexOf(".")
            ),
          });
        } else {
          this.$root.$emit("showAlert", {
            type: "error",
            msg: "Please fill out or select the following required fields: Treatment Details",
          });
          throw new Error("Missing Treatment Information");
        }
      }
      return tprDto;
    },
    createSurgicalCase() {
      var surgicalCaseDto = {};
      surgicalCaseDto.division = this.surgicalCase.division;
      surgicalCaseDto.branch = this.surgicalCase.branch;
      surgicalCaseDto.hospitalName = this.surgicalCase.hospitalName;
      surgicalCaseDto.hospitalNumber = this.surgicalCase.hospitalNumber;
      surgicalCaseDto.branchName = this.surgicalCase.branchName;
      surgicalCaseDto.branchNumber = this.surgicalCase.branchNumber;
      surgicalCaseDto.surgeon = this.surgicalCase.surgeon;
      surgicalCaseDto.coveringReps = this.surgicalCase.coveringReps;
      surgicalCaseDto.procedure = this.surgicalCase.procedure;
      surgicalCaseDto.surgeryStartDateTime =
        this.surgicalCase.surgeryStartDateTime;
      surgicalCaseDto.deliveryDate = this.surgicalCase.deliveryDate;
      surgicalCaseDto.patientId = this.surgicalCase.patientId;
      surgicalCaseDto.operativeSide = this.surgicalCase.operativeSide
        ? this.surgicalCase.operativeSide
        : null;
      surgicalCaseDto.mako = this.surgicalCase.mako;
      surgicalCaseDto.revision = this.surgicalCase.revision;
      surgicalCaseDto.internalComments = this.surgicalCase.internalComments;
      surgicalCaseDto.treatmentPlanRequests =
        this.surgicalCase.treatmentPlanRequests;
      return surgicalCaseDto;
    },
    async cloneClicked() {
      var tprDto = null;
      try {
        this.progressText = "Cloning Treatment Plan Request...";
        tprDto = await this.createTprDto();
        tprDto.treatment.surgeryDate = this.surgicalCase.surgeryStartDateTime;
        const tprIds = this.surgicalCase.treatmentPlanRequests.map((tpr) =>
          tpr.entityDescriptor.substring(
            tpr.entityDescriptor.lastIndexOf("-") + 1,
            tpr.entityDescriptor.length
          )
        );
        tprDto.externalReference =
          this.surgicalCase.metadata.surgicalCaseId +
          "-" +
          (Math.max.apply(Math, tprIds) + 1);
        var tprResponse = null;

        tprResponse =
          await this.$treatmentPlanRequestsApi.treatmentplanrequestsPost(
            "Robotics",
            tprDto
          );
      } catch (e) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg:
            "Error " + e.status + " while cloning the treatment plan request.",
        });
        this.progressText = "";
        return;
      }
      const requestId = tprResponse.requestId;
      var surgicalCaseDto = this.createSurgicalCase();
      surgicalCaseDto.treatmentPlanRequests.push({
        entityId: requestId,
        entityDescriptor: tprDto.externalReference,
      });
      try {
        await this.$surgicalCaseApi.surgicalcasesIdPut(
          this.surgicalCase.metadata.surgicalCaseId,
          surgicalCaseDto
        );
      } catch (e) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: "Error " + e.status + " while updating the surgical case.",
        });
        return;
      }
      this.$root.$emit("showAlert", {
        type: "info",
        msg: "Treatment Plan Request cloned.",
      });
      this.$emit("cloned", requestId);
    },
    async saveTpr() {
      this.progressText = "Saving Treatment Plan Request...";
      var tprDto = null;
      tprDto = await this.createTprDto();
      const tprResponse =
        await this.$treatmentPlanRequestsApi.treatmentplanrequestsIdPut(
          this.tpr.metadata.requestId,
          this.tpr.metadata.dateTimeLastModified,
          tprDto
        );
      this.requestId = tprResponse.requestId;
      this.tpr.metadata = tprResponse;
      // Update Surgical Case
      var surgicalCaseDto = this.createSurgicalCase();

      const selectedSurgeryDateTime = moment(
        this.selectedSurgeryDate + " " + this.selectedSurgeryTime
      );

      // if surgery date changed, we need to update case and all associated requests
      const surgeryDateChanged = !moment(
        surgicalCaseDto.surgeryStartDateTime
      ).isSame(selectedSurgeryDateTime);

      if (surgeryDateChanged)
        surgicalCaseDto.surgeryStartDateTime = selectedSurgeryDateTime;

      await this.$surgicalCaseApi.surgicalcasesIdPut(
        this.surgicalCase.metadata.surgicalCaseId,
        surgicalCaseDto
      );
      if (surgeryDateChanged) {
        surgicalCaseDto.treatmentPlanRequests
          .filter((tpr) => tpr.entityDescriptor !== this.descriptor)
          .map(async (tpr) => {
            // 1. Get the TPR
            let request =
              await this.$treatmentPlanRequestsApi.treatmentplanrequestsGet({
                externalReference: tpr.entityDescriptor,
                includeAcknowledgedRequests: true,
              });
            // Only Update if request is not cancelled
            if (
              request.content[0].metadata.commentedRequestState.requestState !==
              "Cancelled"
            ) {
              request.content[0].treatment.surgeryDate =
                this.selectedSurgeryDate;
              // 2. Update the TPR with the new surgery date
              await this.$treatmentPlanRequestsApi.treatmentplanrequestsIdPut(
                request.content[0].metadata.requestId,
                request.content[0].metadata.dateTimeLastModified,
                request.content[0]
              );
            }
          });
      }
      this.$emit("surgeryDateUpdated", surgicalCaseDto.surgeryStartDateTime);

      this.updateTprHistory(this.requestId);
      this.progressText = "";
    },
    //Available States: Cancelled, Created, Submitted, Rejected, InDesign, TreatmentPlanAvailable, TreatmentPlanDelivered
    async saveClicked() {
      try {
        await this.saveTpr();
        this.$root.$emit("showAlert", {
          type: "info",
          msg: "Treatment Plan Request saved.",
        });
        this.unsavedChanges = false;
      } catch (e) {
        let errorMsg = "";
        // if this is an makosuite-internal error, just display to user
        if (e.message) {
          errorMsg = e.message;
          // if 409 status => Conflict
        } else if (e.status === 409) {
          errorMsg =
            "Treatment Plan Request was changed by another user. Please reload to get the latest version.";
          // else show other status message
        } else if (e.body?.statusMessage) {
          errorMsg = "Save Failed. " + e.body.statusMessage;
          // if no message, just show status
        } else if (e.status) {
          errorMsg = "Save failed with error code: " + e.status;
          // if nothing there, just display generic error
        } else {
          errorMsg = "Save failed.";
        }
        this.$root.$emit("showAlert", {
          type: "error",
          msg: errorMsg,
        });
        // eslint-disable-next-line
        console.error(e);
        this.progressText = "";
        return;
      }
      if (
        this.unsavedTreatmentPlan &&
        (this.status === "Created" ||
          this.status === "Submitted" ||
          this.status === "InDesign" ||
          this.status === "TreatmentPlanDelivered")
      ) {
        try {
          this.unsavedTreatmentPlan = false;
          this.progressText = "Updating Request state...";
          const stateResponse =
            await this.$treatmentPlanRequestsStateApi.treatmentplanrequestsIdStatePut(
              this.requestId,
              "TreatmentPlanAvailable"
            );
          this.tpr.metadata = stateResponse;
          this.status = stateResponse.commentedRequestState.requestState;
          this.statusDisplay = this.adjustStatusDisplay(this.status);
          await this.$surgicalCaseApi.surgicalcasesIdStatePut(
            this.surgicalCase.metadata.surgicalCaseId,
            {
              surgicalCaseState: "Ready for Surgery",
            }
          );
          this.updateTprHistory(this.requestId);
        } catch (e) {
          this.$root.$emit("showAlert", {
            type: "error",
            msg: `Error ${e.status} while updating the Treatment Plan Request`,
          });
          // eslint-disable-next-line
          console.log(e);
          this.progressText = "";
          return;
        }
        this.$root.$emit("showAlert", {
          type: "info",
          msg: "Treatment Plan Request updated.",
        });
        this.progressText = "";
      }
    },
    async submitClicked() {
      // Check if all information is set
      if (
        _.isEmpty(this.selectedOperativeSide) ||
        _.isEmpty(this.selectedProcedure) ||
        _.isEmpty(this.selectedFirstParam) ||
        _.isEmpty(this.selectedSecondParam) ||
        _.isEmpty(this.selectedThirdParam) ||
        _.isEmpty(this.firstName) ||
        _.isEmpty(this.lastName) ||
        _.isEmpty(this.dateOfBirth) ||
        _.isEmpty(this.gender) ||
        _.isEmpty(this.selectedCoveringRep)
      ) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: "Fill out all fields before submitting the Treatment Plan Request",
        });
        this.progressText = "";
        return;
      }

      // Make sure an active patient scan exists before continuing with submit
      var found = false;
      for (var i = 0; i < this.addedPatientScans.length; i++) {
        if (this.addedPatientScans[i].active == true) {
          found = true;
          break;
        }
      }
      if (found == false) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: "Select an active patient scan before submitting the Treatment Plan Request",
        });
        this.progressText = "";
        return;
      }

      // Get Submit confirmation
      const confirmMsg =
        "By accepting, you are submitting your request to Stryker for further processing";
      const submitConfirm = await this.$bvModal.msgBoxConfirm(confirmMsg, {
        title: "",
        size: "md",
        buttonSize: "md",
        okVariant: "primary",
        okTitle: "Accept",
        cancelVariant: "outline-primary",
        cancelTitle: "Cancel",
        footerClass: "p-2",
        hideHeaderClose: false,
        centered: true,
      });
      if (!submitConfirm) return;

      // All information set: Save TPR and Submit
      try {
        await this.saveTpr();
        this.progressText = "Submitting Treatment Plan Request...";
        const stateResponse =
          await this.$treatmentPlanRequestsStateApi.treatmentplanrequestsIdStatePut(
            this.requestId,
            "Submitted"
          );
        this.status = stateResponse.commentedRequestState.requestState;
        this.statusDisplay = this.adjustStatusDisplay(this.status);
        this.tpr.metadata = stateResponse;
      } catch (e) {
        let errorMsg = "";
        // if this is an makosuite-internal error, just display to user
        if (e.message) {
          errorMsg = e.message;
          // if 409 status => Conflict
        } else if (e.status === 409) {
          errorMsg =
            "Treatment Plan Request was changed by another user. Please reload to get the latest version.";
          // else show other status message
        } else if (e.body?.statusMessage) {
          errorMsg = "Save Failed. " + e.body.statusMessage;
          // if no message, just show status
        } else if (e.status) {
          errorMsg = "Save failed with error code: " + e.status;
          // if nothing there, just display generic error
        } else {
          errorMsg = "Save failed.";
        }
        this.$root.$emit("showAlert", {
          type: "error",
          msg: errorMsg,
        });
        // eslint-disable-next-line
        console.error(e);
        this.progressText = "";
        return;
      }
      this.updateTprHistory(this.requestId);
      this.$root.$emit("showAlert", {
        type: "info",
        msg: "Treatment Plan Request submitted",
      });
      this.progressText = "";
      this.unsavedChanges = false;
    },
    async onTprCancelled(reason) {
      this.progressText = "Cancelling Treatment Plan Request...";
      try {
        const stateResponse =
          await this.$treatmentPlanRequestsStateApi.treatmentplanrequestsIdStatePut(
            this.requestId,
            "Cancelled",
            { requestStateComment: reason }
          );
        this.status = stateResponse.commentedRequestState.requestState;
        this.statusDisplay = this.adjustStatusDisplay(this.status);
        this.cancelReason = stateResponse.commentedRequestState.comment;
        this.tpr.metadata = stateResponse;
        await this.updateTprHistory(this.requestId);
      } catch (e) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg:
            "Error " +
            e.status +
            " while cancelling the Treatment Plan Request.",
        });
        // eslint-disable-next-line
        console.log(e);
        this.progressText = "";
        return;
      }
      this.$root.$emit("showAlert", {
        type: "info",
        msg: "Treatment Plan Request cancelled",
      });
      this.status = "Cancelled";
      this.progressText = "";
    },
    readFile(file) {
      return new Promise((resolve) => {
        let reader = new FileReader();
        reader.onload = function () {
          resolve(reader.result);
        };
        reader.readAsArrayBuffer(file);
      });
    },
    async onUploadTreatmentPlan(data) {
      var file = null;
      // 2.1 If Directory, zip it..
      if (data.droppedDirectory.length > 0) {
        try {
          this.progressText = "Zipping Directory...";
          file = await this.zipDirectory(data);
        } catch (e) {
          // eslint-disable-next-line
          console.log(e);
          this.$root.$emit("showAlert", {
            type: "error",
            msg: `Error while zipping treatment plan directory.`,
          });
          this.progressText = "";
          return;
        }

        // 2.2 Else, just add file
      } else if (data.droppedFile) {
        file = data.droppedFile;
      }
      // 3. Upload File
      this.progressText = "Uploading Treatment Plan...";
      var formData = new FormData();
      formData.append("file", file);
      this.uploadMax = file.size;
      const config = {
        onUploadProgress: (progressEvent) => {
          this.uploadProgress = progressEvent.loaded;
        },
      };

      try {
        this.$store.commit("SET_IS_UPLOADING", true);
        const tpResponse = await axios.post(
          "/treatmentplans",
          formData,
          config
        );
        const planMetadata = {
          treatmentPlanId: tpResponse.data.treatmentPlanId,
          treatmentPlanUri: tpResponse.data.treatmentPlanUri,
          treatmentPlanFilename: tpResponse.data.treatmentPlanFilename,
          associatedTreatmentPlanRequest: {
            treatmentPlanRequestId: this.tpr.metadata.requestId,
            treatmentPlanRequestUri: `http://${this.tpr.metadata.requestId}`,
          },
          implantInformationParameters: data.implantInformationParameters,
          dateTimeUploaded: tpResponse.data.dateTimeUploaded,
          uploadedBy: this.$msal.data.user.userName,
        };
        await this.$treatmentPlanMetadataApi.treatmentplansMetadataPost(
          planMetadata
        );
        const addedTreatmentPlans =
          await this.$treatmentPlanMetadataApi.treatmentplansMetadataGet({
            associatedTreatmentPlanRequestId: this.tpr.metadata.requestId,
            latestUploadedOnly: true,
          });
        this.addedTreatmentPlan = addedTreatmentPlans[0];
        this.unsavedTreatmentPlan = true;
      } catch (e) {
        // eslint-disable-next-line
        console.log(e);
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${e.status}, treatment plan upload failed.`,
        });
        this.progressText = "";
        this.$store.commit("SET_IS_UPLOADING", false);
        return;
      }

      // 4. Success notification
      this.progressText = "";
      this.$store.commit("SET_IS_UPLOADING", false);
      this.unsavedChanges = true;
      this.$root.$emit("showAlert", {
        type: "info",
        msg: "Treatment Plan upload complete.",
      });
    },
    async onUploadPatientScan(data) {
      // 1. transfer discovered data
      if (data.discoveredFirstName) this.firstName = data.discoveredFirstName;
      else {
        this.firstName = "";
      }
      if (data.discoveredLastName) this.lastName = data.discoveredLastName;
      else {
        this.lastName = "";
      }
      if (data.discoveredGender) this.gender = data.discoveredGender;
      else {
        this.gender = "";
      }
      if (
        data.discoveredDateOfBirth &&
        data.discoveredDateOfBirth !== "Invalid date"
      ) {
        this.dateOfBirth = data.discoveredDateOfBirth;
      } else {
        this.dateOfBirth = null;
        this.dateOfBirthInput = null;
      }

      var file = null;
      // 2.1 If Directory, zip it..
      if (data.droppedDirectory && data.droppedDirectory.length > 0) {
        try {
          this.progressText = "Zipping Directory...";
          file = await this.zipDirectory(data);
        } catch (e) {
          this.$root.$emit("showAlert", {
            type: "error",
            msg: `Error while zipping patient scan directory.`,
          });
          // eslint-disable-next-line
          console.error(e);
          this.progressText = "";
          return;
        }
        // 2.2 Else, just add file
      } else if (data.droppedFile) {
        file = data.droppedFile;
      }

      // 3. Upload File
      this.progressText = "Uploading Scan...";
      var formData = new FormData();
      formData.append("file", file);
      this.uploadMax = file.size;
      const config = {
        onUploadProgress: (progressEvent) => {
          this.uploadProgress = progressEvent.loaded;
        },
      };
      let patientScanResponse = null;
      try {
        this.$store.commit("SET_IS_UPLOADING", true);
        patientScanResponse = await axios.post(
          "/patientscans",
          formData,
          config
        );
      } catch (e) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${e.status}, scan upload failed.`,
        });
        this.progressText = "";
        this.$store.commit("SET_IS_UPLOADING", false);
      }

      // Initialize the metadata response array based on if uploaded scan is bilateral
      let metadataResponse = new Array(patientScanResponse.data.length);

      // 4. get metadata and add to attached list
      // Set the old active patient scan to inactive
      this.addedPatientScans = this.addedPatientScans.map((el) =>
        el.active ? { ...el, active: false } : el
      );
      rax.attach();
      try {
        // If bilateral scan need to get the metadata for each one
        var i;
        for (i = 0; i < metadataResponse.length; i++) {
          metadataResponse[i] = await axios({
            url: `patientscans/${patientScanResponse.data[i].patientScanId}/metadata`,
            raxConfig: {
              retryDelay: 5000,
              statusCodesToRetry: [[400, 429]],
              backoffType: "static",
            },
          });
        }
      } catch (error) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${error.status}, retrieving patient scan information failed.`,
        });
        this.progressText = "";
        this.$store.commit("SET_IS_UPLOADING", false);
        return;
      }
      // Set the new scan to active if it's not a bilateral scan
      var newScanActive = true;
      if (metadataResponse.length > 1) {
        newScanActive = false;
      }
      // Add the new scan(s) to the top
      i = 0;
      for (i = 0; i < metadataResponse.length; i++) {
        this.addedPatientScans.unshift({
          id: metadataResponse[i].data.metadata.patientScanId,
          patientScan: metadataResponse[i].data.metadata.patientScanDescriptor,
          assignedBy: metadataResponse[i].data.metadata.uploadedByUser,
          uploadedDate: moment(
            metadataResponse[i].data.metadata.dateTimeUploaded
          ).format("l"),
          active: newScanActive,
          provisioningStatus: "InProgress",
        });
      }

      // 5. Success notification
      this.progressText = "";
      this.$store.commit("SET_IS_UPLOADING", false);
      this.unsavedChanges = true;
      this.$root.$emit("showAlert", {
        type: "info",
        msg: "Scan upload complete.",
      });

      // Warn the user if this is a bilateral scan
      if (metadataResponse.length > 1) {
        // Wait 3s before sending the next alert so user can see the first one
        await new Promise((r) => setTimeout(r, 3000));
        this.$root.$emit("showAlert", {
          type: "warning",
          msg: `We detected a bilateral scan for ${data.fullName}`,
        });
      }
    },
    implantInfoToString(implantInformationParameters) {
      let implants = "";
      implantInformationParameters.forEach((param) => {
        const implantInfo = this.applicationImplantData.filter(
          (data) => data.value === param.key
        );
        if (implantInfo) {
          implants += implantInfo[0]["text"];
          implants += ": ";
          implants += param.value;
          implants += ", ";
        }
      });
      implants = implants.slice(0, -2);
      return implants;
    },
    async getStatusesForScans(updateAllScans = false) {
      // Don't make backend calls if there aren't any patient scans to check
      if (this.addedPatientScans.length == 0) {
        return;
      }
      // Create an array with all scans that should be updated
      let patientScanIds = this.addedPatientScans
        .filter(
          (scan) => scan.provisioningStatus === "InProgress" || updateAllScans
        )
        .map((scan) => scan.id);

      // If no scans to update, just return
      if (!patientScanIds.length) return;

      // Get Statuses for all Scans in the array
      const metadataResponse =
        await this.$patientScanViewerProvisioningApi.patientscansViewermetadataGet(
          { patientScanIds: patientScanIds, pageSize: 0 }
        );

      // Update Statuses and show success alert if needed
      metadataResponse.content.map((response) => {
        const scan =
          this.addedPatientScans[
            this.addedPatientScans.findIndex(
              (el) => el.id === response.patientScanId
            )
          ];
        // If this update resulted in a completed scan and we're not still initializing, show a success alert
        if (
          response.provisioningStatus === "Completed" &&
          scan.provisioningStatus === "InProgress" &&
          this.statusChecksTimer !== null
        ) {
          this.$root.$emit("showAlert", {
            type: "info",
            msg:
              "Patient Scan loading for " +
              scan.patientScan +
              " complete. Ready to view patient scan.",
          });
          // If this update resulted in a failed scan and we're not still initializing, show a failure alert
        } else if (
          response.provisioningStatus === "Failed" &&
          scan.provisioningStatus === "InProgress" &&
          this.statusChecksTimer !== null
        ) {
          this.$root.$emit("showAlert", {
            type: "error",
            msg:
              "Patient Scan loading for " +
              scan.patientScan +
              " failed. Click to retry",
          });
        }
        // Finally, update the provisioning Status
        this.addedPatientScans[
          this.addedPatientScans.findIndex(
            (el) => el.id === response.patientScanId
          )
        ]["provisioningStatus"] = response.provisioningStatus;
      });
    },
    async startBackendStatusChecks() {
      // Once every updateScansWithLoadingStatusInSeconds seconds, we check the (provisioning) statuses of the scans
      this.statusChecksTPRTimer = setInterval(() => {
        if (document.visibilityState === "visible") {
          // Count the total time the tab is visible
          this.totalTimeVisible += this.$updateScansWithLoadingStatusInSeconds;
          // Every time the tab is visible for $updateAllScansInSeconds, we make a call to update all scans
          this.getStatusesForScans(
            this.totalTimeVisible % this.$updateAllScansInSeconds <=
              this.$updateScansWithLoadingStatusInSeconds
          );
        }
      }, this.$updateScansWithLoadingStatusInSeconds * 1000);
    },
    handleDicomButtonClick(item) {
      // Only try to open the dicom viewer if the scan succeeded
      if (item["provisioningStatus"] === "Completed") {
        this.openDicomViewer(item);
      } else if (item["provisioningStatus"] === "Unprovisioned") {
        this.addedPatientScans = this.addedPatientScans.map((scan) =>
          scan.id === item.id
            ? { ...scan, provisioningStatus: "InProgress" }
            : scan
        );
        this.provisionDicomViewer(item);
      } else if (item["provisioningStatus"] === "Failed") {
        item.provisioningStatus = "InProgress";
        this.provisionDicomViewer(item);
      } else {
        // eslint-disable-next-line
        console.error(
          "Unimplemented provisioning status: " +
            item["provisioningStatus"] +
            " button click"
        );
      }
    },
    provisionDicomViewer(item) {
      try {
        this.$patientScanViewerProvisioningApi.patientscansIdViewerGet(item.id);
      } catch (error) {
        // Otherwise if error in backend, log it out
        let errorMsg = "";
        if (error.status === 409) {
          errorMsg = `Another scan with the same Study ID was detected, provisioning aborted`;
        } else {
          errorMsg = `Error ${error.status} while provisioning Patient Scan ${item.id}`;
        }
        this.showSpinner = false;
        this.$root.$emit("showAlert", {
          type: "error",
          msg: errorMsg,
        });
      }
    },
    async openDicomViewer(item) {
      // Show Loading status while we wait for backend
      item.provisioningStatus = "InProgress";
      try {
        // Try to get the URL of the viewer from the backend
        var response_obj =
          await this.$patientScanViewerProvisioningApi.patientscansIdViewerGet(
            item.id
          );
        var ohif_url = response_obj.ohifUrl;
        item.provisioningStatus = "Completed";

        // Open and focus on the viewer in a new tab
        var win = window.open(ohif_url, "_blank");
        win.focus();
      } catch (error) {
        // Otherwise if error in backend, log it out
        let errorMsg = "";
        if (error.status === 409) {
          errorMsg = `Another scan with the same Study ID was detected, provisioning aborted`;
        } else {
          errorMsg = `Error ${error.status} while provisioning Patient Scan ${item.id}`;
        }
        this.showSpinner = false;
        this.$root.$emit("showAlert", {
          type: "error",
          msg: errorMsg,
        });
      }
    },
    adjustStatusDisplay(tprStatus) {
      var newStatus = "";
      if (tprStatus == "TreatmentPlanAvailable") {
        newStatus = "Treatment Plan Available";
      } else if (tprStatus == "TreatmentPlanDelivered") {
        newStatus = "Treatment Plan Downloaded";
      } else if (tprStatus == "InDesign") {
        newStatus = "In Design";
      } else {
        newStatus = tprStatus;
      }
      return newStatus;
    },
  },
  watch: {
    secondParamList() {
      // preselect if only a single element in list
      if (this.secondParamList.length === 1)
        this.selectedSecondParam = this.secondParamList[0].value;
      // If the complete list changed and the current selection is no longer in the list, set back to null
      else if (
        this.secondParamList.filter(
          (param) => param.value == this.selectedSecondParam
        ).length === 0
      )
        this.selectedSecondParam = null;
    },
    thirdParamList() {
      // preselect if only a single element in list
      if (this.thirdParamList.length === 1)
        this.selectedThirdParam = this.thirdParamList[0].value;
      // If the complete list changed and the current selection is no longer in the list, set back to null
      else if (
        this.thirdParamList.filter(
          (param) => param.value == this.selectedThirdParam
        ).length === 0
      )
        this.selectedThirdParam = null;
    },
    checkForUnsavedChanges() {
      const currentChangeString = `${this.selectedCoveringRep}|${this.selectedSurgeryDate}|${this.selectedSurgeryTime}|${this.selectedOperativeSide}|${this.selectedProcedure}|${this.selectedFirstParam}|${this.selectedSecondParam}|${this.selectedThirdParam}|${this.neededBy}|${this.firstName}|${this.lastName}|${this.dateOfBirth}|${this.gender}|${this.addedPatientScans}|${this.internalComments}`;
      if (this.changeString === currentChangeString) {
        this.unsavedChanges = false;
      } else {
        this.unsavedChanges = true;
      }

      this.changeString = currentChangeString;
    },
  },
  computed: {
    uploadInProgress() {
      if (
        this.progressText.includes("Uploading") ||
        this.progressText.includes("Zipping")
      ) {
        return true;
      } else {
        return false;
      }
    },
    tprIsCreatedOrRejected() {
      if (this.status === "Created" || this.status === "Rejected") {
        return true;
      }
      return false;
    },
    tprIsCancelled() {
      return this.status === "Cancelled";
    },
    tprWasSubmitted() {
      return (
        this.status === "Submitted" ||
        this.status === "InDesign" ||
        this.status === "TreatmentPlanAvailable" ||
        this.status === "TreatmentPlanDelivered"
      );
    },
    checkForUnsavedChanges() {
      return `${this.selectedCoveringRep}|${this.selectedSurgeryDate}|${this.selectedSurgeryTime}|${this.selectedOperativeSide}|${this.selectedProcedure}|${this.selectedFirstParam}|${this.selectedSecondParam}|${this.selectedThirdParam}|${this.neededBy}|${this.firstName}|${this.lastName}|${this.dateOfBirth}|${this.gender}|${this.addedPatientScans}|${this.internalComments}`;
    },
    applicationImplantData() {
      if (this.selectedProcedure) {
        return this.$store.getters.implantData(this.selectedProcedure);
      }
      return null;
    },
    patientDetailsFilled() {
      return this.firstName &&
        !_.isEmpty(this.firstName) &&
        this.lastName &&
        !_.isEmpty(this.lastName) &&
        this.dateOfBirth &&
        !_.isEmpty(this.dateOfBirth) &&
        this.gender &&
        !_.isEmpty(this.gender)
        ? true
        : false;
    },
    // Labels
    firstParamLabel() {
      return this.$store.getters.paramLabels(this.selectedProcedure)[0];
    },
    secondParamLabel() {
      return this.$store.getters.paramLabels(this.selectedProcedure)[1];
    },
    thirdParamLabel() {
      return this.$store.getters.paramLabels(this.selectedProcedure)[2];
    },
    // Param Lists
    firstParamList() {
      if (this.selectedProcedure) {
        return this.$store.getters.firstParamList(this.selectedProcedure);
      }
      return [];
    },
    secondParamList() {
      if (this.selectedFirstParam) {
        return this.$store.getters.secondParamList(
          this.selectedProcedure,
          this.selectedFirstParam
        );
      }
      return [];
    },
    thirdParamList() {
      if (this.selectedSecondParam) {
        return this.$store.getters.thirdParamList(
          this.selectedProcedure,
          this.selectedFirstParam,
          this.selectedSecondParam
        );
      }
      return [];
    },
    addedTreatmentPlanComputed() {
      if (this.addedTreatmentPlan && this.applicationImplantData) {
        const implantInfo = this.implantInfoToString(
          this.addedTreatmentPlan.implantInformationParameters
        );
        return [
          {
            treatmentPlanId: this.addedTreatmentPlan.treatmentPlanId,
            fileName: this.addedTreatmentPlan.treatmentPlanFilename,
            assignedBy: this.addedTreatmentPlan.uploadedBy,
            implantSizes: implantInfo,
          },
        ];
      } else {
        return null;
      }
    },
    branch() {
      return (
        this.surgicalCase.branchNumber + " - " + this.surgicalCase.branchName
      );
    },
    hospitalName() {
      if (_.some(this.surgicalCase.hospitalName, _.isNotEmpty))
        return (
          this.surgicalCase.hospitalNumber +
          " - " +
          this.surgicalCase.hospitalName
        );
      else return "";
    },
    surgeonName() {
      if (_.some(this.surgicalCase.surgeon, _.isNotEmpty))
        return (
          this.surgicalCase.surgeon.email +
          " - " +
          this.surgicalCase.surgeon.firstName +
          " " +
          this.surgicalCase.surgeon.lastName
        );
      else return "";
    },
    coveringReps() {
      var reps = [];
      this.surgicalCase.coveringReps.forEach((rep) => {
        reps.push({
          text: rep.firstName + " " + rep.lastName,
          value: rep.email,
        });
      });
      // If active primary rep is deleted from surgical case, we still want to keep it
      if (
        this.tpr?.primaryContact &&
        !reps.some((rep) => rep.value === this.tpr?.primaryContact.email) &&
        this.status !== "Created" &&
        this.statues !== "Rejected"
      ) {
        reps.push({
          text:
            this.tpr.primaryContact.firstName +
            " " +
            this.tpr.primaryContact.lastName,
          value: this.tpr.primaryContact.email,
        });
      }
      return reps;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~bootstrap/scss/functions", "@/assets/_stryker.scss";
.progressText {
  color: $primary;
  font-size: large;
}
#idDeletePatientScansBtn {
  float: right;
  margin-top: -0.5rem;
  margin-right: 1rem;
}
.addTreatmentPlanRequest {
  margin: 1rem;
}
.btn {
  margin: 0.2rem 0.2rem;
}
.viewer-btn {
  width: 9rem;
}
label {
  margin-top: 1rem;
  margin-bottom: 0;
}
#idCaseDetails {
  font-size: small;
}
li {
  text-align: left;
}
hr {
  border-top: 5px solid black;
}
#attachPatientScan {
  float: right;
}
.card {
  margin: 2rem 0rem;
  text-align: left;
}
.row {
  margin: 0.8rem 0;
}

.modal > label {
  margin-top: 0.3rem;
}

.unsaved-changes {
  color: #ffb500;
  position: absolute;
  left: 1rem;
}

.changes-saved {
  color: #29542c;
  position: absolute;
  left: 1rem;
}
</style>